import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { ChevronRight, RefreshCw, ShieldAlert, ArrowUpRight } from "lucide-react";

const INCIDENT_LABELS = {
  violence: "Violence", ballot_snatching: "Ballot Snatching", over_voting: "Over-voting",
  obstruction: "Obstruction", equipment_malfunction: "Equipment", bribery: "Vote-buying",
  intimidation: "Intimidation", misconduct: "Misconduct", media_denied: "Media Denied",
  underage_voting: "Under-age", impersonation: "Impersonation", other: "Other",
};
const SEV_COLORS = { low: "#0EA5E9", medium: "#F59E0B", high: "#EF4444", critical: "#A855F7" };
import BrandMark from "@/components/BrandMark";

function Counter({ value }) {
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  useEffect(() => { displayRef.current = display; }, [display]);
  useEffect(() => {
    let start = displayRef.current, end = Number(value) || 0, dur = 800, t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setDisplay(Math.floor(start + (end - start) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className="tabular">{display.toLocaleString()}</span>;
}

export default function PublicDashboard() {
  const [elections, setElections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [collation, setCollation] = useState(null);
  const [stateBreakdown, setStateBreakdown] = useState([]);
  const [recent, setRecent] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [includePending, setIncludePending] = useState(false);
  const { user } = useAuth();
  const [incidents, setIncidents] = useState({ by_category: {}, by_severity: {}, total: 0 });
  const [fraud, setFraud] = useState({ total_flagged: 0, total_results: 0, critical: 0, high: 0 });
  const [publicTimeline, setPublicTimeline] = useState([]);
  const [approvedResults, setApprovedResults] = useState([]);
  const [approvedTotals, setApprovedTotals] = useState({ total_approved: 0, total_pu: 0 });
  const [approvedFilter, setApprovedFilter] = useState({ region: "", constituency: "", q: "" });
  const [regionCoverage, setRegionCoverage] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [leaderTrend, setLeaderTrend] = useState([]);
  const [sheetPreview, setSheetPreview] = useState(null); // { rid, pu }
  const [wsLive, setWsLive] = useState(false);

  const loadElections = async () => {
    const { data } = await api.get("/elections");
    setElections(data);
    if (data.length && !selected) setSelected(data.find(e => e.status === "active") || data[0]);
  };

  const load = async () => {
    if (!selected) return;
    setRefreshing(true);
    try {
      const [nat, byState, recentRes, incRes, fraudRes, tlRes, apprRes] = await Promise.all([
        api.get(`/elections/${selected.id}/collation`, { params: { level: "national", include_pending: includePending } }),
        api.get(`/elections/${selected.id}/collation`, { params: { level: "state", include_pending: includePending } }),
        api.get("/results", { params: { election_id: selected.id } }),
        api.get("/public/incidents/summary").catch(() => ({ data: { by_category: {}, by_severity: {}, total: 0 } })),
        api.get("/public/fraud/summary").catch(() => ({ data: { total_flagged: 0, total_results: 0, critical: 0, high: 0 } })),
        api.get("/public/timeline", { params: { limit: 40 }}).catch(() => ({ data: { events: [] } })),
        api.get("/public/approved-results", { params: { limit: 48, region: approvedFilter.region || undefined, constituency: approvedFilter.constituency || undefined, q: approvedFilter.q || undefined }}).catch(() => ({ data: { results: [], total_approved: 0, total_pu: 0 } })),
      ]);
      setCollation(nat.data);
      setStateBreakdown(byState.data.buckets || []);
      setRecent(recentRes.data.slice(0, 30));
      setIncidents(incRes.data || { by_category: {}, by_severity: {}, total: 0 });
      setFraud(fraudRes.data || { total_flagged: 0, total_results: 0, critical: 0, high: 0 });
      setPublicTimeline(tlRes.data?.events || []);
      setApprovedResults(apprRes.data?.results || []);
      setApprovedTotals({ total_approved: apprRes.data?.total_approved || 0, total_pu: apprRes.data?.total_pu || 0 });
      setRegionCoverage(apprRes.data?.region_coverage || []);
      try {
        const [{ data: cons }, { data: trend }] = await Promise.all([
          api.get("/public/constituencies").catch(() => ({ data: [] })),
          api.get("/public/leader-trend").catch(() => ({ data: { buckets: [] } })),
        ]);
        setConstituencies(cons || []);
        setLeaderTrend(trend?.buckets || []);
      } catch {}
    } catch {} finally { setRefreshing(false); }
  };

  // Live WebSocket — prepend events onto the public ribbon as they happen
  useEffect(() => {
    if (!selected) return;
    const url = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") + `/api/ws/collation/${selected.id}` + (localStorage.getItem("ec_token") ? `?token=${encodeURIComponent(localStorage.getItem("ec_token"))}` : "");
    let ws;
    try {
      ws = new WebSocket(url);
      ws.onopen = () => setWsLive(true);
      ws.onclose = () => setWsLive(false);
      ws.onmessage = (ev) => {
        let m = null; try { m = JSON.parse(ev.data); } catch { return; }
        // Any result state change triggers a full refresh so vote counts stay accurate
        if (["result.submit", "result.approve", "result.reject"].includes(m.type)) load();
        // Prepend a placeholder to the approved grid on approve — full refresh fills the details in.
        if (m.type === "result.approve") {
          setApprovedResults(prev => {
            if (prev.some(x => x.id === m.result_id)) return prev;
            return [{ id: m.result_id || `pending-${Date.now()}`, polling_unit: { code: "…", name: "New approval landing…" }, region: "", constituency: "", _pending: true }, ...prev].slice(0, 48);
          });
          setApprovedTotals(t => ({ ...t, total_approved: (t.total_approved || 0) + 1 }));
        }
        // Prepend to the public ribbon for instant visual feedback (approve/reject/incident)
        if (["result.approve", "result.reject", "incident.create"].includes(m.type)) {
          setPublicTimeline(prev => [{
            at: new Date().toISOString(),
            kind: m.type,
            region: "", constituency: "",
            category: m.category, severity: m.severity,
          }, ...prev].slice(0, 60));
        }
      };
    } catch {}
    return () => { try { ws && ws.close(); } catch {} };
  }, [selected?.id]);

  useEffect(() => { loadElections(); }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [selected?.id, includePending, approvedFilter.region, approvedFilter.constituency, approvedFilter.q]);

  const candidates = collation?.candidates || [];
  const totals = collation?.totals || {};
  const meta = collation?.meta || {};
  const totalVotes = candidates.reduce((s, c) => s + (totals[c.id] || 0), 0);
  const ranked = useMemo(() => [...candidates].sort((a, b) => (totals[b.id]||0) - (totals[a.id]||0)), [candidates, totals]);
  const leader = ranked[0];
  const chartData = ranked.map(c => ({ name: c.party_code || c.name.slice(0,10), votes: totals[c.id] || 0, color: c.color }));
  const reportPct = collation?.total_polling_units ? Math.min(100, Math.round((meta.polling_units_reported||0) / collation.total_polling_units * 100)) : 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-zinc-900 relative">
      <div className="noise-overlay" />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex items-center flex-wrap gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <BrandMark variant="lockup" size={52} />
            <div className="hidden sm:flex items-center gap-2 pl-3 ml-1 border-l border-black/10">
              <span className="live-dot" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Live · Election Broadcast</div>
            </div>
          </div>
          <div className="flex-1 min-w-0" />
          <div className="hidden lg:flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
            <span className="live-dot" /><span>Live · 10s</span>
          </div>
          {user && (
          <button
            onClick={() => setIncludePending(v => !v)}
            data-testid="toggle-pending-btn"
            title={includePending ? "Showing pending + approved" : "Showing approved only"}
            className={`text-[11px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] px-2.5 sm:px-3 py-2 border transition-colors ${includePending ? "border-amber-500/60 bg-amber-100 text-amber-700" : "border-black/15 hover:bg-black/[0.05]"}`}>
            {includePending ? "+ Pending" : "Approved"}
          </button>
          )}
          <select
            data-testid="election-select"
            value={selected?.id || ""}
            onChange={(e) => setSelected(elections.find(el => el.id === e.target.value))}
            className="bg-transparent border border-black/15 px-2 sm:px-3 py-2 text-xs sm:text-sm outline-none hover:border-black/40 max-w-[140px] sm:max-w-none">
            {elections.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            {!elections.length && <option>No elections</option>}
          </select>
          <button onClick={load} data-testid="refresh-btn" className="p-2 border border-black/15 hover:bg-black/[0.05] transition-colors">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          </button>
          <Link to={user ? "/app" : "/login"} data-testid="signin-link" className="text-[11px] sm:text-xs uppercase tracking-[0.2em] px-3 sm:px-4 py-2 border border-black/15 hover:bg-black hover:text-white transition-colors">
            {user ? "Console" : "Sign in"}
          </Link>
        </div>
      </header>

      {!selected && (
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 text-center relative">
          <img src="https://images.unsplash.com/photo-1777428411691-e4d5c3423ed0?crop=entropy&cs=srgb&fm=jpg&w=1400" alt="" className="mx-auto w-full max-w-2xl opacity-40" />
          <h1 className="font-display text-5xl sm:text-6xl mt-8">No elections yet</h1>
          <p className="text-zinc-400 mt-4 max-w-md mx-auto">Sign in to the operations console to configure your first election, candidates and polling-unit hierarchy.</p>
          <Link to="/login" className="inline-block mt-8 px-6 py-3 bg-white text-black text-sm uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200 transition-colors">Open Console</Link>
        </div>
      )}

      {selected && (
        <>
          {/* Hero band */}
          <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-10 pb-6 relative">
            <div className="flex items-baseline gap-4 flex-wrap">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Now Reporting</div>
              <div className="text-xs uppercase tracking-[0.3em] text-emerald-400">{selected.election_type}</div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">· {new Date(selected.election_date).toDateString?.() || selected.election_date}</div>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-3 leading-[0.9]" data-testid="election-title">{selected.name}</h1>
          </div>

          {/* Grid */}
          <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pb-12 grid grid-cols-12 gap-6">
            {/* Leader card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="col-span-12 lg:col-span-8 relative border border-black/5 bg-white p-8 lg:p-12 overflow-hidden"
              style={leader ? { boxShadow: `0 0 0 1px ${leader.color}30, 0 0 48px -8px ${leader.color}40` } : {}}
              data-testid="leader-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500">
                <span className="live-dot" /> Current Leader
              </div>
              {leader ? (
                <>
                  <div className="mt-4 flex items-start gap-5">
                    {leader.photo_url ? (
                      <img src={leader.photo_url} alt={leader.name} className="w-28 h-28 lg:w-36 lg:h-36 object-cover border-4" style={{ borderColor: leader.color }}/>
                    ) : (
                      <div className="w-2 h-24 rounded" style={{ background: leader.color }} />
                    )}
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] flex items-center gap-2" style={{ color: leader.color }}>
                        {leader.party_symbol && (leader.party_symbol.startsWith("http") ? <img src={leader.party_symbol} alt="" className="w-5 h-5"/> : <span className="text-lg">{leader.party_symbol}</span>)}
                        <span>{leader.party} · {leader.party_code}</span>
                      </div>
                      <div className="font-display text-4xl lg:text-6xl mt-1 leading-[0.95]">{leader.name}</div>
                      {leaderTrend.length > 0 && (
                        <svg viewBox="0 0 240 40" className="mt-3 w-56 h-10" data-testid="leader-sparkline">
                          {(() => {
                            const max = Math.max(1, ...leaderTrend.map(b => b.cum || 0));
                            const pts = leaderTrend.map((b, i) => `${(i / Math.max(1, leaderTrend.length - 1)) * 240},${40 - ((b.cum || 0) / max) * 36 - 2}`).join(" ");
                            return (<>
                              <polyline fill="none" stroke={leader.color || "#10B981"} strokeWidth="2" points={pts}/>
                              <polyline fill={(leader.color || "#10B981") + "22"} stroke="none" points={`0,40 ${pts} 240,40`}/>
                            </>);
                          })()}
                        </svg>
                      )}
                      {leaderTrend.length > 0 && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Approvals · last 24h</div>}
                    </div>
                  </div>
                  <div className="mt-8 flex items-end gap-6 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Votes</div>
                      <div className="font-display text-6xl lg:text-8xl mt-1"><Counter value={totals[leader.id] || 0} /></div>
                    </div>
                    <div className="pb-2">
                      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Share</div>
                      <div className="font-display text-3xl tabular mt-1">{totalVotes ? ((totals[leader.id]||0)/totalVotes*100).toFixed(1) : "0.0"}%</div>
                    </div>
                    <div className="pb-2">
                      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Margin</div>
                      <div className="font-display text-3xl tabular mt-1">+<Counter value={(totals[leader.id]||0) - (totals[ranked[1]?.id]||0)} /></div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-6 text-zinc-500">Waiting for first results...</div>
              )}
            </motion.div>

            {/* Reporting stats */}
            <div className="col-span-12 lg:col-span-4 grid grid-rows-2 gap-6">
              <div className="border border-black/5 bg-white p-6" data-testid="reporting-card">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Polling Units Reported</div>
                <div className="mt-3 flex items-end gap-3">
                  <div className="font-display text-5xl"><Counter value={meta.polling_units_reported || 0} /></div>
                  <div className="text-zinc-500 pb-2 text-sm">/ {(collation?.total_polling_units || 0).toLocaleString()}</div>
                </div>
                <div className="mt-4 h-1 bg-black/[0.06] relative">
                  <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-700" style={{ width: `${reportPct}%` }} />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-2">{reportPct}% Reporting</div>
              </div>
              <div className="border border-black/5 bg-white p-6">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Turnout Metrics</div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><div className="text-xs text-zinc-500">Registered</div><div className="font-display text-2xl tabular"><Counter value={meta.registered||0}/></div></div>
                  <div><div className="text-xs text-zinc-500">Accredited</div><div className="font-display text-2xl tabular"><Counter value={meta.accredited||0}/></div></div>
                  <div><div className="text-xs text-zinc-500">Valid</div><div className="font-display text-2xl tabular text-emerald-400"><Counter value={meta.valid||0}/></div></div>
                  <div><div className="text-xs text-zinc-500">Rejected</div><div className="font-display text-2xl tabular text-red-400"><Counter value={meta.rejected||0}/></div></div>
                </div>
              </div>
            </div>

            {/* Candidate ladder */}
            <div className="col-span-12 lg:col-span-7 border border-black/5 bg-white" data-testid="candidate-ladder">
              <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Standings</div>
                <div className="text-xs text-zinc-500">{totalVotes.toLocaleString()} total votes</div>
              </div>
              <div className="divide-y divide-black/5">
                {ranked.map((c, i) => {
                  const v = totals[c.id] || 0;
                  const pct = totalVotes ? (v/totalVotes*100) : 0;
                  return (
                    <div key={c.id} className="px-6 py-4 flex items-center gap-4 hover:bg-black/[0.02] transition-colors">
                      <div className="font-display text-2xl tabular text-zinc-500 w-8">{String(i+1).padStart(2,"0")}</div>
                      <div className="w-1.5 h-10" style={{ background: c.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-zinc-500 uppercase tracking-wider">{c.party} · {c.party_code}</div>
                      </div>
                      <div className="hidden sm:block flex-1">
                        <div className="h-1.5 bg-black/[0.04]"><div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: c.color }} /></div>
                      </div>
                      <div className="text-right w-32">
                        <div className="font-display text-xl tabular"><Counter value={v} /></div>
                        <div className="text-xs text-zinc-500 tabular">{pct.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
                {!ranked.length && <div className="p-6 text-zinc-500">No candidates configured.</div>}
              </div>
            </div>

            {/* Chart */}
            <div className="col-span-12 lg:col-span-5 border border-black/5 bg-white p-6" data-testid="results-chart">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Vote Distribution</div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                    <XAxis dataKey="name" stroke="#71717a" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                    <YAxis stroke="#71717a" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 0 }} labelStyle={{ color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Bar dataKey="votes" radius={[0,0,0,0]}>
                      {chartData.map((d) => <Cell key={d.name || d.id} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Incident pulse — hidden from public dashboard (still visible to admins in the console). */}

            {/* State breakdown */}
            <div className="col-span-12 border border-black/5 bg-white" data-testid="state-breakdown">
              <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Regional Breakdown</div>
                <div className="text-xs text-zinc-500">{stateBreakdown.length} regions reporting</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 grid-tech">
                {stateBreakdown.map((b) => {
                  const bTotals = b.totals || {};
                  const rankedLocal = [...candidates].sort((a, b2) => (bTotals[b2.id]||0) - (bTotals[a.id]||0));
                  const local = rankedLocal[0];
                  const localTotal = candidates.reduce((s,c)=> s + (bTotals[c.id]||0), 0);
                  const localVotes = local ? bTotals[local.id]||0 : 0;
                  const localPct = localTotal ? (localVotes/localTotal*100).toFixed(1) : "0.0";
                  return (
                    <Link key={b.location_id} to={`/place/${b.location_id}`} className="p-5 hover:bg-black/[0.03] transition-colors block" data-testid={`region-card-${b.location_id}`}>
                      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{b.location}</div>
                      <div className="mt-2 flex items-center gap-2">
                        {local && <span className="w-1 h-8" style={{ background: local.color }} />}
                        <div>
                          <div className="font-display text-lg leading-none">{local?.name || "—"}</div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{local?.party_code || ""}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-end justify-between">
                        <div className="font-display text-2xl tabular">{localVotes.toLocaleString()}</div>
                        <div className="text-xs text-zinc-500 tabular">{localPct}% · {b.polling_units_reported} PU</div>
                      </div>
                    </Link>
                  );
                })}
                {!stateBreakdown.length && <div className="p-8 text-zinc-500 col-span-full">No regional data yet.</div>}
              </div>
            </div>
          </div>

            {/* Public Timeline Ribbon */}
            <div className="col-span-12 border border-black/5 bg-white" data-testid="approved-results-grid">
              <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                  <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Approved Polling-Unit Results</div>
                  <div className="text-[11px] text-zinc-400">{approvedResults.length} shown</div>
                </div>
                <a href={`${(process.env.REACT_APP_BACKEND_URL||'').replace(/\/$/,'')}/api/public/approved-results.csv`}
                  data-testid="approved-csv-btn"
                  className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 border border-emerald-600/30 hover:bg-emerald-50 px-3 py-1 flex items-center gap-1.5">
                  <ArrowUpRight size={11}/> Download CSV
                </a>
              </div>

              {/* Coverage headline */}
              <div className="px-6 py-5 border-b border-black/5 flex items-baseline gap-3 flex-wrap" data-testid="approved-coverage">
                <span className="font-display text-4xl tabular text-zinc-900">{approvedTotals.total_approved.toLocaleString()}</span>
                <span className="text-sm text-zinc-500">of {approvedTotals.total_pu.toLocaleString()} polling units approved</span>
                <span className="ml-auto text-[11px] uppercase tracking-[0.2em] px-2 py-1 bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 tabular">
                  {approvedTotals.total_pu ? ((approvedTotals.total_approved / approvedTotals.total_pu) * 100).toFixed(2) : "0.00"}% coverage
                </span>
              </div>

              {/* Region coverage ranks */}
              {regionCoverage.length > 0 && (
                <div className="px-6 py-3 border-b border-black/5 flex items-center gap-2 overflow-x-auto" data-testid="region-coverage-ranks">
                  {regionCoverage.map(r => (
                    <div key={r.region} className="min-w-[130px] border border-black/10 px-3 py-2" data-testid={`region-rank-${r.region}`}>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{r.region}</div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-display text-lg tabular text-zinc-900">{r.pct}%</span>
                        <span className="text-[10px] text-zinc-400 tabular">{r.approved.toLocaleString()}/{r.total.toLocaleString()}</span>
                      </div>
                      <div className="mt-1 h-0.5 bg-black/5 relative">
                        <div className="absolute inset-y-0 left-0 bg-emerald-500" style={{ width: `${r.pct}%` }}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Search & filter */}
              <div className="px-6 py-3 border-b border-black/5 flex items-center gap-2 flex-wrap" data-testid="approved-filters">
                <input value={approvedFilter.q} onChange={e=>setApprovedFilter(f=>({...f, q: e.target.value}))}
                  data-testid="approved-search" placeholder="Search PU name or code…"
                  className="flex-1 min-w-[220px] bg-transparent border border-black/10 px-3 py-2 text-sm outline-none focus:border-black"/>
                <select value={approvedFilter.region} onChange={e=>setApprovedFilter(f=>({...f, region: e.target.value}))}
                  data-testid="approved-region"
                  className="bg-transparent border border-black/10 px-3 py-2 text-sm outline-none focus:border-black">
                  <option value="">All regions</option>
                  {Array.from(new Set(stateBreakdown.map(b => b.location?.name).filter(Boolean))).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <input value={approvedFilter.constituency} onChange={e=>setApprovedFilter(f=>({...f, constituency: e.target.value}))}
                  data-testid="approved-constituency" placeholder="Constituency…" list="constituency-list"
                  className="w-56 bg-transparent border border-black/10 px-3 py-2 text-sm outline-none focus:border-black"/>
                <datalist id="constituency-list">
                  {constituencies.map(c => <option key={c} value={c}/>)}
                </datalist>
                {(approvedFilter.q || approvedFilter.region || approvedFilter.constituency) && (
                  <button onClick={()=>setApprovedFilter({region:"", constituency:"", q:""})} data-testid="approved-clear-filters"
                    className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-black border border-black/10 px-2 py-1">Clear</button>
                )}
              </div>
              {approvedResults.length === 0 ? (
                <div className="p-8 text-zinc-400 text-sm">No approved polling units yet. Results appear here the moment a reviewer approves them.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-tech">
                  {approvedResults.map(r => (
                    <div key={r.id} onClick={() => r.has_sheet && setSheetPreview({ rid: r.id, pu: r.polling_unit })} className={`p-4 transition-colors ${r._pending?"bg-emerald-50/70 animate-pulse":"hover:bg-black/[0.02]"} ${r.has_sheet?"cursor-pointer":""}`} data-testid={`approved-pu-${r.polling_unit.code}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-emerald-700 tabular text-[11px] font-semibold uppercase tracking-wider">{r.polling_unit.code}</div>
                          <div className="text-sm mt-0.5 font-medium text-zinc-800 truncate">{r.polling_unit.name}</div>
                          <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{r.region}{r.constituency?` · ${r.constituency}`:""}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="text-[9px] uppercase tracking-widest text-emerald-700 border border-emerald-600/30 bg-emerald-50 px-1.5 py-0.5">Approved</div>
                          {r.has_sheet && <div className="text-[9px] uppercase tracking-widest text-zinc-500">tap to view sheet</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Public Timeline Ribbon — hidden from public dashboard (available to signed-in users on /app/timeline). */}

          {/* Ticker */}
          <div className="border-t border-black/5 bg-zinc-100 py-3">
            <Marquee gradient={false} speed={40} pauseOnHover className="text-sm">
              {fraud.total_flagged > 0 && (
                <span className="mx-8 text-amber-700 flex items-center" data-testid="fraud-ticker">
                  <span className="inline-block w-2 h-2 mr-2 bg-amber-500"/>
                  <span className="uppercase text-xs tracking-[0.2em] mr-2 font-semibold">Under review</span>
                  {fraud.total_flagged} of {fraud.total_results} submission{fraud.total_flagged===1?"":"s"} pending verification
                  {fraud.critical > 0 && <span className="ml-2 text-red-700">· {fraud.critical} critical</span>}
                  {fraud.high > 0 && <span className="ml-2 text-red-600">· {fraud.high} high</span>}
                </span>
              )}
              {recent.length ? recent.map((r) => (
                <span key={r.id} className="mx-8 text-zinc-300">
                  <span className={`inline-block w-2 h-2 mr-2 ${r.status==="approved"?"bg-emerald-500":r.status==="rejected"?"bg-red-500":"bg-amber-400"}`} />
                  <span className="uppercase text-xs tracking-[0.2em] mr-2">{r.status}</span>
                  Result submitted <ChevronRight className="inline" size={12}/> {(r.submitted_by_email||"").split("@")[0]}
                </span>
              )) : <span className="mx-8 text-zinc-500 text-xs uppercase tracking-[0.3em]">Awaiting first transmission from polling units...</span>}
            </Marquee>
          </div>

          {/* Sheet preview modal — watermarked EC8A image full-screen */}
          {sheetPreview && (
            <div
              onClick={() => setSheetPreview(null)}
              data-testid="sheet-preview-modal"
              className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200">
              <div onClick={(e)=>e.stopPropagation()} className="relative max-w-5xl w-full max-h-full bg-white border border-white/10 shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-black/10 bg-white">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-700">EC8A · Approved Sheet</div>
                    <div className="text-sm font-semibold text-zinc-900 truncate">{sheetPreview.pu?.code} · {sheetPreview.pu?.name}</div>
                  </div>
                  <button
                    onClick={() => setSheetPreview(null)}
                    data-testid="sheet-preview-close"
                    className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-black/15 hover:bg-black hover:text-white transition-colors">Close</button>
                </div>
                <div className="relative overflow-auto bg-zinc-950 flex items-center justify-center" style={{ minHeight: "60vh" }}>
                  <img
                    src={`${(process.env.REACT_APP_BACKEND_URL||'').replace(/\/$/,'')}/api/public/approved-sheet/${sheetPreview.rid}`}
                    alt={`EC8A sheet for ${sheetPreview.pu?.code}`}
                    data-testid="sheet-preview-image"
                    className="max-w-full max-h-[80vh] object-contain"
                    onError={(e)=>{ e.currentTarget.style.display='none'; }}/>
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                    <div className="text-white/10 font-display text-6xl sm:text-8xl rotate-[-24deg] tracking-widest select-none whitespace-nowrap">COLLATION WATCH</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
